import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import { ASSET_NAME } from './ProviderLandingPage.duck';
import css from './ProviderLandingPage.module.css';

import WhatSection from './WhatSection/WhatSection';
import WhySection from './WhySection/WhySection';
import HowSection from './HowSection/HowSection';
import CommunitySection from './CommunitySection/CommunitySection';
import TypeSection from './TypeSection/TypeSection';
import ReadySection from './ReadySection/ReadySection';
import FAQSection from './FAQSection/FAQSection';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const ProviderLandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  // Page data from Sharetribe console
  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // All the text and images for the sections
  const sections = pageData?.sections;

  const what = sections?.filter(section => section.sectionName === 'what');
  const whatSection = {
    sectionId: 'what',
    sectionType: 'what',
    sectionData: what,
  };

  const why = sections?.filter(section => section.sectionName === 'why');
  const whySection = {
    sectionId: 'why',
    sectionType: 'why',
    sectionData: why,
  };

  const how = sections?.filter(section => section.sectionName === 'how');
  const howSection = {
    sectionId: 'how',
    sectionType: 'how',
    sectionData: how,
  };

  const ready = sections?.filter(section => section.sectionName === 'ready-to-start');
  const readySection = {
    sectionId: 'ready',
    sectionType: 'ready',
    sectionData: ready,
  };

  const community = sections?.filter(section => section.sectionName === 'community');
  const communityReview = sections?.filter(section => section.sectionName === 'community-review');
  const communityListing = sections?.filter(section => section.sectionName === 'community-listing');

  const communitySection = {
    sectionId: 'community',
    sectionType: 'community',
    sectionData: community,
    reviewData: communityReview,
    listingData: communityListing,
  };

  const type = sections?.filter(section => section.sectionName === 'type');
  const typeSection = {
    sectionId: 'type',
    sectionType: 'type',
    sectionData: type,
  };

  // Contains the title and individual faq sections
  const faq = sections?.filter(section => section.sectionName === 'faq');

  // Contains the text for the faq cta
  const faqCta = sections?.filter(section => section.sectionName === 'faq-cta');

  const faqSection = {
    sectionId: 'faq',
    sectionType: 'faq',
    sectionData: faq,
    secondaryData: faqCta,
  };

  const customSections = pageData ? [whatSection, whySection, howSection, communitySection, typeSection, readySection, faqSection] : pageData?.sections;

  return (
    <PageBuilder
      pageAssetsData={{
        ...pageData,
        sections: customSections,
      }}
      options={{
        sectionComponents: {
          what: {
            component: WhatSection,
          },
          why: {
            component: WhySection,
          },
          how: {
            component: HowSection,
          },
          community: {
            component: CommunitySection,
          },
          type: {
            component: TypeSection,
          },
          ready: {
            component: ReadySection,
          },
          faq: {
            component: FAQSection,
          },
        },
      }}
      inProgress={inProgress}
      error={error}
    />
  );
};

ProviderLandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ProviderLandingPage = compose(connect(mapStateToProps))(ProviderLandingPageComponent);

export default ProviderLandingPage;
